import * as React from 'react';
import { ChangeEvent } from 'react';
import { connect } from 'react-redux';
import { IDocumentViewerItem, IReport, IReportType } from '../../common/sharedTypes';
import * as urls from '../shared/urls';
import {
  setShowSidebar,
  setShowHeader,
} from '../../components/Application/redux-objects/actions';
import { getAllDocuments, updateResults } from './redux-objects/actions';
import { Accordion, Card, Row, Col, Tab, Tabs, Form, Button } from 'react-bootstrap';
import WorkflowTrigger from '../Workflow/WorkflowTrigger';
import { b64toBlob } from '../PrintPDF/common/utils';
import moment from 'moment';
import './assets/documentViewer.css';
import CommentsButton from '../Comments/CommentsButton';
import { DatabasesToSearch } from '../shared/databasesToSearch';
import CommentModal from '../CommentModal/CommentModal';
import { toast } from 'react-toastify';
import Api from '../../services/Api';
import { ApiResponse } from 'apisauce';
import { UserInfo, userIsAdmin } from '../Login/common/defaults';
import { REPORT_TYPE_BAC_ID, REPORT_TYPE_CONTINUATION_ID, REPORT_TYPE_DRIVER_ID, REPORT_TYPE_FATAL_SUPPLEMENT_ID, REPORT_TYPE_INVESTIGATOR_ID, REPORT_TYPE_LETTERS_MEMOS_ID, REPORT_TYPE_NON_MOTORIST_SUPPLEMENT_ID, REPORT_TYPE_OTHER_ID, REPORT_TYPE_PHOTOS_ID, REPORT_TYPE_SPD_ID, REPORT_TYPE_TRUCK_BUS_SUPPLEMENT } from '../shared/constants';

export interface IDocumentViewerProps {
  setShowSidebar?: (showSidebar: boolean) => void;
  setShowHeader?: (showHeader: boolean) => void;
  getAllDocuments?: (url: string) => Promise<IDocumentViewerItem[]>;
  updateResults?: (items: IDocumentViewerItem[]) => Promise<IDocumentViewerItem[]>;
  items?: IDocumentViewerItem[];
  currentUser?: UserInfo;
}

export interface IDocumentViewerState {
  loading: boolean;
  managementMode: boolean;
  showCommentModal: boolean;
  comment: string;

  editingDocIdReportId: number | null;
  editingSortOrderReportId: number | null;
  editingReportTypeReportId: number | null;

  editingDocumentContext: string | null;
  loadError: string | null;

  editedDocumentIdValues: { [key: number]: string };
  editedDocumentSortOrderValues: { [key: number]: number };
  editingReportTypeValues: { [key: number]: IReportType | null };
}

const initialState: IDocumentViewerState = {
  loading: true,
  managementMode: true,
  showCommentModal: false,
  comment: "",
  editingDocIdReportId: null,
  editingSortOrderReportId: null,
  editingReportTypeReportId: null,

  editingDocumentContext: null,
  loadError: null,

  editedDocumentIdValues: {},
  editedDocumentSortOrderValues: {},
  editingReportTypeValues: {},
};

const REPORT_TYPE_OPTIONS: IReportType[] = [
  { id: REPORT_TYPE_BAC_ID, name: "Blood and Alcohol (BAC)", description: "Purpose not provided" },
  { id: REPORT_TYPE_DRIVER_ID, name: "Driver", description: "Report for drivers" },
  { id: REPORT_TYPE_FATAL_SUPPLEMENT_ID, name: "Fatal Supplement", description: "Purpose not provided" },
  { id: REPORT_TYPE_INVESTIGATOR_ID, name: "Investigator", description: "Report for investigators" },
  { id: REPORT_TYPE_LETTERS_MEMOS_ID, name: "Letters & Memos", description: "Purpose not provided" },
  { id: REPORT_TYPE_NON_MOTORIST_SUPPLEMENT_ID, name: "Non-Motorist Supplement", description: "Purpose not provided" },
  { id: REPORT_TYPE_OTHER_ID, name: "Other", description: "Purpose not provided" },
  { id: REPORT_TYPE_PHOTOS_ID, name: "Photos", description: "Purpose not provided" },
  { id: REPORT_TYPE_TRUCK_BUS_SUPPLEMENT, name: "Truck / Bus Supplement", description: "Purpose not provided" },
  { id: REPORT_TYPE_SPD_ID, name: "State Property Damage (SPD)", description: "Purpose not provided" },
  { id: REPORT_TYPE_CONTINUATION_ID, name: "Continuation", description: "Purpose not provided" },
]

class DocumentViewer extends React.Component<
  IDocumentViewerProps,
  IDocumentViewerState
> {
  public constructor(props: IDocumentViewerProps) {
    super(props);
    this.state = initialState;
  }

  private reportIdToRemove: number;

  componentDidMount() {
    this.props.setShowSidebar(false);
    this.props.setShowHeader(false);
    const urlParams = new URLSearchParams(window.location.search);
    let reportId = urlParams.get('r');
    this.setState({ managementMode: urlParams.get('m') == 'm' });
    let dbToSearch: DatabasesToSearch = urlParams.get('dbToSearch') as DatabasesToSearch ?? DatabasesToSearch.Private;
    let uri = urls.DOC_VIEWER_GET_ALL_DOCUMENTS + `?reportId=${reportId}&dbToSearch=${dbToSearch}`;
    this.props.getAllDocuments(uri)
      .then(() => {
        this.setState({ loading: false, loadError: null });
      })
      .catch((ex) => {
        console.error('DocumentViewer failed to load documents', {
          reportId,
          dbToSearch,
          uri,
          error: ex,
        });
        this.setState({
          loading: false,
          loadError: `Unable to load documents for report ${reportId}. Check the console and network response for details.`,
        });
      });
  }

  handleDelete = (id: number) => {
    this.reportIdToRemove = id;
    this.setState({
      showCommentModal: true
    });
  };

  cancelComment = () => {
    this.setState({
      comment: "",
      showCommentModal: false
    });
  };

  closeModel = () => {
    this.setState({
      showCommentModal: false,
      comment: ""
    });
  };

  updateComment = (e: React.FormEvent) => {
    let el: HTMLFormElement = e.currentTarget as HTMLFormElement;
    this.setState({
      comment: el.value
    });
  };

  saveComment = () => {
    this.removeReport(this.reportIdToRemove);
    //this.createManagementLog();
    this.closeModel();
  };

  getPdfDiagnostics = (
    base64Data: string,
    label: string,
    item: IDocumentViewerItem,
    source?: string
  ) => {
    const diagnostics = {
      objectUrl: null as string | null,
      isValidPdf: false,
      hasData: !!base64Data && base64Data.trim().length > 0,
      byteLength: 0,
      base64Length: base64Data?.length || 0,
      header: '',
      error: '',
    };

    if (!diagnostics.hasData) {
      diagnostics.error = `${label} PDF data was empty.`;
      console.warn('DocumentViewer empty PDF data', {
        label,
        reportId: item.reportId,
        documentId: item.documentId,
        reportType: item.reportType?.name,
        source,
      });
      return diagnostics;
    }

    try {
      const normalizedBase64 = base64Data.includes(',')
        ? base64Data.substring(base64Data.indexOf(',') + 1)
        : base64Data;
      const compactBase64 = normalizedBase64.replace(/\s/g, '');
      const decoded = atob(compactBase64);
      diagnostics.byteLength = decoded.length;
      diagnostics.header = decoded.substring(0, 12).replace(/[^\x20-\x7E]/g, '.');
      diagnostics.isValidPdf = decoded.startsWith('%PDF-');

      if (!diagnostics.isValidPdf) {
        diagnostics.error = `${label} data decoded, but does not start with %PDF-.`;
      }

      diagnostics.objectUrl = URL.createObjectURL(b64toBlob(compactBase64, 'application/pdf'));

      if (!diagnostics.isValidPdf) {
        console.warn('DocumentViewer non-PDF payload', {
          label,
          reportId: item.reportId,
          documentId: item.documentId,
          reportType: item.reportType?.name,
          source,
          byteLength: diagnostics.byteLength,
          base64Length: diagnostics.base64Length,
          header: diagnostics.header,
        });
      }
    } catch (ex) {
      diagnostics.error = `${label} data was not valid base64: ${ex?.message || ex}`;
      console.error('DocumentViewer invalid PDF payload', {
        label,
        reportId: item.reportId,
        documentId: item.documentId,
        reportType: item.reportType?.name,
        source,
        base64Length: diagnostics.base64Length,
        error: ex,
      });
    }

    return diagnostics;
  };

  renderPdfDiagnostics = (diagnostics, label: string, item: IDocumentViewerItem, source?: string) => {
    if (diagnostics.isValidPdf) return null;

    return (
      <div className='alert alert-warning' role='alert'>
        <strong>{label} could not be verified as a PDF.</strong>
        <br />
        Report ID: {item.reportId}; Document ID: {item.documentId || 'none'}; Source: {source || 'unknown'}
        <br />
        {diagnostics.error}
        <br />
        Base64 length: {diagnostics.base64Length}; decoded bytes: {diagnostics.byteLength}; header: {diagnostics.header || 'none'}
      </div>
    );
  };

  startEditingDocumentId = (reportId: number, currentDocumentId: string, context: string) => {
    this.setState({
      editingDocIdReportId: reportId,
      editingDocumentContext: context,
      editedDocumentIdValues: {
        ...this.state.editedDocumentIdValues,
        [reportId]: currentDocumentId
      }
    });
  };

  cancelEditingDocumentId = () => {
    this.setState({
      editingDocIdReportId: null,
      editingDocumentContext: null
    });
  };

  updateEditedDocumentId = (reportId: number, e: React.ChangeEvent<any>) => {
    this.setState({
      editedDocumentIdValues: {
        ...this.state.editedDocumentIdValues,
        [reportId]: e.currentTarget.value
      }
    });
  };

  saveDocumentId = (reportId: number) => {
    const item = this.props.items.find(x => x.reportId === reportId);
    if (!item) return;

    const updatedItems = this.props.items.map(x =>
      x.reportId === reportId
        ? { ...x, documentId: this.state.editedDocumentIdValues[reportId] }
        : x
    );

    return Api.post(urls.UPDATE_DOCUMENT_ID_VIA_DOC_VIEWER, {
      reportId: reportId,
      documentId: this.state.editedDocumentIdValues[reportId]
    }).then((result: ApiResponse<any>) => {
      if (result.data) {
        if (result.data.success) {
          toast.success("Document ID updated");
          this.setState({
            editingDocIdReportId: null,
            editingDocumentContext: null
          });

          this.props.updateResults(updatedItems);

          // Refetch the documents with the new document ID
          this.refetchDocuments();
        } else {
          toast.error("Failed to Update Document ID");
        }
      }
    });
  }

  startEditingDocumentSortOrder = (reportId: number, currentSortOrder: number, context: string) => {
    this.setState({
      editingSortOrderReportId: reportId,
      editingDocumentContext: context,
      editedDocumentSortOrderValues: {
        ...this.state.editedDocumentSortOrderValues,
        [reportId]: currentSortOrder
      }
    })
  };

  cancelEditingDocumentSortOrder = () => {
    this.setState({
      editingSortOrderReportId: null,
      editingDocumentContext: null
    });
  };
  updateEditedDocumentSortOrder = (reportId: number, e: React.ChangeEvent<any>) => {
    const inputValue = e.currentTarget.value;
    const newSortOrder = inputValue === '' ? 0 : Number(inputValue);

    if (isNaN(newSortOrder)) return;

    this.setState({
      editedDocumentSortOrderValues: {
        ...this.state.editedDocumentSortOrderValues,
        [reportId]: newSortOrder
      }
    });
  };

  saveDocumentSortOrder = (reportId: number) => {
    const item = this.props.items.find(x => x.reportId === reportId);
    if (!item) return;

    const updatedItems = this.props.items.map(x =>
      x.reportId === reportId
        ? { ...x, sortOrder: this.state.editedDocumentSortOrderValues[reportId] }
        : x
    );

    return Api.post(urls.UPDATE_DOCUMENT_SORT_ORDER_VIA_DOC_VIEWER, {
      reportId: reportId,
      sortOrder: this.state.editedDocumentSortOrderValues[reportId]
    }).then((result: ApiResponse<any>) => {
      if (result.data) {
        if (result.data.success) {
          toast.success("Document Sort Order updated");
          this.setState({
            editingSortOrderReportId: null,
            editingDocumentContext: null
          });

          this.props.updateResults(updatedItems);

          // Refetch the documents with the new sort order
          this.refetchDocuments();
        } else {
          toast.error("Failed to Update Document Sort Order");
        }
      }
    });
  };

  startEditingReportType = (reportId: number, currentReportType: IReportType, context: string) => {
    this.setState({
      editingReportTypeReportId: reportId,
      editingDocumentContext: context,
      editingReportTypeValues: {
        ...this.state.editingReportTypeValues,
        [reportId]: currentReportType
      }
    });
  };

  updateEditedReportType = (reportId: number, e: React.ChangeEvent<any>) => {
    const newTypeId = Number(e.currentTarget.value);
    const newType = REPORT_TYPE_OPTIONS.find(t => t.id === newTypeId);

    if (!newType) return;

    this.setState({
      editingReportTypeValues: {
        ...this.state.editingReportTypeValues,
        [reportId]: newType
      }
    });
  };

  saveReportType = (reportId: number) => {
    const item = this.props.items?.find(x => x.reportId === reportId);
    if (!item) return;

    const newReportType = this.state.editingReportTypeValues[reportId];
    if (newReportType === undefined) return;

    const updatedItems = this.props.items.map(x =>
      x.reportId === reportId
        ? { ...x, reportType: newReportType }
        : x
    );

    return Api.post(urls.UPDATE_REPORT_TYPE_VIA_DOC_VIEWER, {
      reportId,
      reportTypeId: newReportType.id
    }).then((result: ApiResponse<any>) => {
      if (result.data?.success) {
        toast.success("Report Type Updated");
        this.setState({
          editingReportTypeReportId: null,
          editingDocumentContext: null
        });
        this.props.updateResults(updatedItems);
        this.refetchDocuments();
      } else {
        toast.error("Failed to update Report Type");
      }
    });
  }

  cancelEditingReportType = () => {
    this.setState({
      editingReportTypeReportId: null,
      editingDocumentContext: null
    });
  }

  refetchDocuments = () => {
    const urlParams = new URLSearchParams(window.location.search);
    let reportId = urlParams.get('r');
    let dbToSearch: DatabasesToSearch = urlParams.get('dbToSearch') as DatabasesToSearch ?? DatabasesToSearch.Private;
    let uri = urls.DOC_VIEWER_GET_ALL_DOCUMENTS + `?reportId=${reportId}&dbToSearch=${dbToSearch}`;
    this.setState({ loading: true });
    this.props.getAllDocuments(uri).then(() => {
      this.setState({ loading: false });
    });
  };

  renderDocumentIdField = (item: IDocumentViewerItem, context: string) => {
    const isAdmin = this.props.currentUser && userIsAdmin(this.props.currentUser);
    const isEditing = this.state.editingDocIdReportId === item.reportId && this.state.editingDocumentContext === context;

    if (isAdmin && isEditing) {
      return (
        <Form.Group className="mb-0">
          <div className="d-flex gap-2">
            <Form.Control
              type="text"
              value={this.state.editedDocumentIdValues[item.reportId] ?? item.documentId ?? ''}
              onChange={(e) => this.updateEditedDocumentId(item.reportId, e)}
              size="sm"
            />
            &nbsp;
            <Button
              variant="outline-success"
              size="sm"
              onClick={() => this.saveDocumentId(item.reportId)}
            >
              Save
            </Button>
            &nbsp;
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={this.cancelEditingDocumentId}
            >
              Cancel
            </Button>

          </div>
        </Form.Group>
      );
    }

    return (
      <div className="d-flex gap-2 align-items-center">
        <Form.Label className="mb-0 mr-2">
          Document ID : {item.documentId}
        </Form.Label>
        {isAdmin && (
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => this.startEditingDocumentId(item.reportId, item.documentId, context)}
          >
            Edit
          </Button>
        )}
      </div>
    );
  };

  renderSortOrder = (item: IDocumentViewerItem, context: string) => {
    const isAdmin = this.props.currentUser && userIsAdmin(this.props.currentUser);
    const isEditing = this.state.editingSortOrderReportId === item.reportId && this.state.editingDocumentContext === context;

    if (isAdmin && isEditing) {
      return (
        <Form.Group className="mb-0">
          <div className="d-flex gap-2">
            <Form.Control
              type="text"
              value={this.state.editedDocumentSortOrderValues[item.reportId] ?? item.sortOrder ?? '999'}
              onChange={(e) => this.updateEditedDocumentSortOrder(item.reportId, e)}
              size="sm"
            />
            &nbsp;
            <Button
              variant="outline-success"
              size="sm"
              onClick={() => this.saveDocumentSortOrder(item.reportId)}
            >
              Save
            </Button>
            &nbsp;
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={this.cancelEditingDocumentSortOrder}
            >
              Cancel
            </Button>

          </div>
        </Form.Group>
      );
    }

    return (
      <div className="d-flex gap-2 align-items-center">
        <Form.Label className="mb-0 mr-2">
          Sort Order : {item.sortOrder}
        </Form.Label>
        {isAdmin && (
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => this.startEditingDocumentSortOrder(item.reportId, item.sortOrder, context)}
          >
            Edit
          </Button>
        )}
      </div>
    );
  };

  renderReportTypeField = (item: IDocumentViewerItem, context: string) => {
    const isAdmin = this.props.currentUser && userIsAdmin(this.props.currentUser);
    const isEditing = this.state.editingReportTypeReportId === item.reportId && this.state.editingDocumentContext === context;

    if (isAdmin && isEditing) {
      const currentType = this.state.editingReportTypeValues[item.reportId] ?? item.reportType ?? null;

      return (
        <Form.Group className="mb-0">
          <div className="d-flex gap-2 align-items-center">
            <Form.Control as="select"
              value={currentType?.id ?? 0}
              onChange={(e) => this.updateEditedReportType(item.reportId, e)}
              size="sm"
            >
              <option value={0} disabled>Select Report Type</option>
              {REPORT_TYPE_OPTIONS.map(type => (
                <option key={type.id} value={type.id}>
                  {type.name}
                </option>
              ))}
            </Form.Control>

            <Button
              variant="outline-success"
              size="sm"
              onClick={() => this.saveReportType(item.reportId)}>
              Save
            </Button>

            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => this.cancelEditingReportType()}>
              Cancel
            </Button>
          </div>
        </Form.Group>
      );
    }
    const displayName = item.reportType?.name ?? `Unknown (${item.reportType.id})`;

    return (
      <div className="d-flex gap-2 align-items-center">
        <Form.Label className="mb-0 mr-0">
          Report Type: {displayName}
        </Form.Label>
        {isAdmin && (
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => this.startEditingReportType(item.reportId, item.reportType, context)}
          >
            Edit
          </Button>
        )}
      </div>
    );
  }

  removeReport = (reportIdToRemove: number) => {
    //Just send over the workflowItemId and handle the reports on the backend
    return Api.post(urls.REMOVE_VIA_DOC_VIEWER, {
      reportId: reportIdToRemove,
      comment: this.state.comment
    }).then((result: ApiResponse<any>) => {
      if (result.data) {
        if (result.data.success) {
          toast.success("Report Deleted");
          console.debug("Delete:", result);
          this.props.updateResults(this.props.items.filter(x => x.reportId != reportIdToRemove))
        } else {
          toast.error("Failed to Delete");
          console.debug("Delete:", result);
        }
      }

      // remove report from list of reports

    });
  }

  public render() {
    let thisReport: IDocumentViewerItem = null;
    if (this.props.items && this.props.items.length > 0) {
      const urlParams = new URLSearchParams(window.location.search);
      let reportId = Number.parseInt(urlParams.get('r'));
      thisReport = this.props.items.filter(
        (x) => x.reportId == reportId)[0];

      const thisIndex = this.props.items.findIndex(x => x.reportId == reportId);
      this.props.items.unshift(...this.props.items.splice(thisIndex, 1));
    }
    return (
      <React.Fragment>

        <CommentModal
          comment={this.state.comment}
          show={this.state.showCommentModal}
          onCommentChange={this.updateComment}
          onCloseCommentModal={this.cancelComment}
          onCancel={this.cancelComment}
          onSave={this.saveComment}
          title='Remove Report'
          description='Comment for removing this associated report'
        />

        {this.state.loading && <div className='divAad'>Loading</div>}
        {!this.state.loading && this.state.loadError && (
          <div className='alert alert-danger' role='alert'>
            {this.state.loadError}
          </div>
        )}
        {!this.state.loading && !this.state.loadError && (
          <div className='divDocumentViewer'>
            {this.props.items && this.props.items.length > 0 && (
              <div>
                <div>
                  <Row>
                    <Col xs="2">
                      <b>Related Documents: </b>
                    </Col>
                    <Col xs="5">
                      <Form.Label>
                        Received / Created :{' '}
                        {moment(thisReport.createdDate).format('LLL')}
                      </Form.Label>
                    </Col>
                    <Col xs="2">
                      <Form.Label>
                        Original Crash Key : {thisReport.originalCrashKey}
                      </Form.Label>
                    </Col>
                    <Col xs="3">
                      <Row>
                        {this.renderDocumentIdField(thisReport, 'header')}
                      </Row>
                      <Row>
                        {this.renderSortOrder(thisReport, 'header')}
                      </Row>
                      <Row>
                        {this.renderReportTypeField(thisReport, 'header')}
                      </Row>
                    </Col>
                  </Row>
                </div>
                <Tabs
                  defaultActiveKey={this.props.items[0].frontSheetData != null
                    && (this.props.items[0].reportType?.name == "Investigator"
                      || this.props.items[0].reportType?.name == "Driver")
                    ? "frontsheet" + this.props.items[0].reportId : this.props.items[0].reportId}
                  id='trigger-tabs'
                >

                  {this.props.items.map((item: IDocumentViewerItem, index) => {
                    const mainPdf = this.getPdfDiagnostics(item.data, 'Report', item, item.source);
                    let tabs = [];

                    if (item.frontSheetData != null && (item.reportType?.name == "Investigator" ||
                      item.reportType?.name == "Driver")) {
                      const frontSheetPdf = this.getPdfDiagnostics(item.frontSheetData, 'Front sheet', item, item.frontSheetSource);
                      tabs.push(
                        <Tab
                          eventKey={"frontsheet" + item.reportId.toString()}
                          title="Front Sheet"
                          key={"frontsheet" + item.reportId}
                        >
                          <div>
                            <Row>
                              <Col>
                                <Form.Label>
                                  Received / Created :{' '}
                                  {moment(item.createdDate).format('LLL')}
                                </Form.Label>
                              </Col>
                              <Col>
                                <Form.Label>
                                  Crash Key : {item.crashKey}
                                </Form.Label>
                              </Col>
                              <Col>
                                <Row>
                                  {this.renderDocumentIdField(item, 'frontsheet')}
                                </Row>
                                <Row>
                                  {this.renderSortOrder(item, 'frontsheet')}
                                </Row>
                                <Row>
                                  {this.renderReportTypeField(item, 'frontsheet')}
                                </Row>
                              </Col>
                              <Col xs={1} className='justify-content-end'>
                                <CommentsButton
                                  reportId={item.reportId}
                                  report={item.report}
                                  className='pull-right'
                                  placement='left'
                                />
                              </Col>
                            </Row>
                            <hr />
                          </div>
                          {this.renderPdfDiagnostics(frontSheetPdf, 'Front sheet', item, item.frontSheetSource)}
                          {frontSheetPdf.objectUrl && (
                            <iframe
                              width='99%'
                              height='99%'
                              src={`${frontSheetPdf.objectUrl}#zoom=FitH`}
                            > </iframe>
                          )}
                        </Tab>
                      )
                    }

                    if (item.pdfDifferences != null && (item.reportType?.name == "Investigator" ||
                      item.reportType?.name == "Driver")) {
                      const pdfDifferences = this.getPdfDiagnostics(item.pdfDifferences, 'Highlighted changes', item, item.source);
                      tabs.push(
                        <Tab
                          eventKey={"highlightedChanges" + item.reportId.toString()}
                          title={"Highlighted " + item.reportType?.name.toString() + " Report Changes"}
                          key={"highlightedChanges" + item.reportId}
                        >
                          <div>
                            <Row>
                              <Col>
                                <Form.Label>
                                  Received / Created :{' '}
                                  {moment(item.createdDate).format('LLL')}
                                </Form.Label>
                              </Col>
                              <Col>
                                <Form.Label>
                                  Crash Key : {item.crashKey}
                                  {item.comparedToCrashKey && (
                                    <React.Fragment>
                                      <br />
                                      Compared To : {item.comparedToCrashKey}
                                    </React.Fragment>
                                  )}
                                </Form.Label>
                              </Col>
                              <Col>
                                <Row>
                                  {this.renderDocumentIdField(item, 'hightlighted')}
                                </Row>
                                <Row>                                  
                                    {this.renderSortOrder(item, 'highlighted')}
                                  </Row>
                                  <Row>
                                    {this.renderReportTypeField(item, 'highlighted')}
                                </Row>
                              </Col>
                              <Col xs={1} className='justify-content-end'>
                                <CommentsButton
                                  reportId={item.reportId}
                                  report={item.report}
                                  className='pull-right'
                                  placement='left'
                                />
                              </Col>
                            </Row>
                            <hr />
                          </div>
                          {this.renderPdfDiagnostics(pdfDifferences, 'Highlighted changes', item, item.source)}
                          {pdfDifferences.objectUrl && (
                            <iframe
                              width='99%'
                              height='99%'
                              src={`${pdfDifferences.objectUrl}#zoom=FitH`}
                            > </iframe>
                          )}
                        </Tab>
                      )
                    }
                    tabs.push(
                      <Tab
                        eventKey={item.reportId.toString()}
                        title={
                          item.reportId == thisReport.reportId
                            ? (item.reportType?.name ||
                              'Unknown') + ' (This Report)'
                            : item.reportType?.name || 'Unknown'
                        }
                        key={item.reportId}
                      >
                        <div>
                          <Row>
                            <Col>
                              <Form.Label>
                                Received / Created :{' '}
                                {moment(item.createdDate).format('LLL')}
                              </Form.Label>
                            </Col>
                            <Col>
                              <Form.Label>
                                Crash Key : {item.crashKey}
                              </Form.Label>
                            </Col>
                            <Col>
                              <Row>
                                {this.renderDocumentIdField(item, 'main')}
                              </Row>
                              <Row>
                                  {this.renderSortOrder(item, 'main')}
                                  </Row>
                                  <Row>
                                  {this.renderReportTypeField(item, 'main')}
                              </Row>
                            </Col>
                            <Col xs={2} className='justify-content-end'>
                              {this.state.managementMode && thisReport.reportId != item.reportId && (
                                <React.Fragment>
                                  {"   "}
                                  <Button
                                    variant="outline-danger"
                                    size="sm"
                                    onClick={() => this.handleDelete(item.reportId)}
                                  >
                                    Delete
                                  </Button>
                                </React.Fragment>)}

                              <CommentsButton
                                reportId={item.reportId}
                                report={item.report}
                                className='pull-right'
                                placement='left'
                              />
                              {"   "}
                            </Col>
                          </Row>
                          <hr />
                        </div>
                        {this.renderPdfDiagnostics(mainPdf, 'Report', item, item.source)}
                        {(mainPdf.objectUrl || item.fileURL) && (
                          <iframe
                            width='99%'
                            height='99%'
                            src={`${mainPdf.objectUrl || item.fileURL}#zoom=FitH`}
                          > </iframe>
                        )}
                      </Tab>
                    );

                    return tabs;
                  })}

                </Tabs>
              </div>
            )}
          </div>
        )}
      </React.Fragment>
    );
  }
}

export function mapStateToProps(state: any) {
  return {
    currentUser: state.Application.currentUser,
    items: state.DocumentViewer.items,
  };
}

const mapDispatchToProps = {
  setShowSidebar,
  setShowHeader,
  getAllDocuments,
  updateResults,
};

export default connect(mapStateToProps, mapDispatchToProps)(DocumentViewer);
